import { AxiosError, AxiosResponse } from 'axios'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query'

import * as requests from './requests'
import {
  TStaffListItem,
  TParams,
  TStoresListItem,
  TWalletsManagementDetails,
  TLeadersListItem,
  TConcludeParams,
  TManageWalletParams,
  TOpenWalletsPeriodParams,
  TWalletsManagementPeriod,
  TStoreWithoutSubsidiaryParams,
  TWalletsManagementReport
} from './types'
import { ResponseError, ResponseSuccess } from '../../types'

export const KEY_STAFF_LIST_QUERY = 'getStaffList'

export const KEY_LEADERS_LIST_QUERY = 'getLeadersList'

export const KEY_STORES_LIST_QUERY = 'getStoresList'

export const KEY_WALLETS_MANAGEMENT_DETAILS_QUERY = 'getWalletsManagementDetails'

export const KEY_WALLETS_MANAGEMENT_PERIOD_QUERY = 'getWalletsManagementPeriod'

export const KEY_WALLETS_MANAGEMENT_REPORT_QUERY = 'getWalletsManagementReport'

export const useGetStaffListInfinityQuery = ({ enabled = true, ...params }: TParams) =>
  useInfiniteQuery<AxiosResponse<ResponseSuccess<TStaffListItem[]>>, AxiosError<ResponseError>>(
    [KEY_STAFF_LIST_QUERY, params],
    ({ pageParam }) => requests.getStaffList({ ...params, page: pageParam }),
    {
      enabled,
      getNextPageParam: (lastPage) =>
        lastPage.data.metadata.pagination.current_page < lastPage.data.metadata.pagination.last_page
          ? lastPage.data.metadata.pagination.current_page + 1
          : false
    }
  )

export const useGetLeadersListInfinityQuery = ({ enabled = true, ...params }: TParams) =>
  useInfiniteQuery<AxiosResponse<ResponseSuccess<TLeadersListItem[]>>, AxiosError<ResponseError>>(
    [KEY_LEADERS_LIST_QUERY, params],
    ({ pageParam }) => requests.getLeadersList({ ...params, page: pageParam }),
    {
      enabled,
      getNextPageParam: (lastPage) =>
        lastPage.data.metadata.pagination.current_page < lastPage.data.metadata.pagination.last_page
          ? lastPage.data.metadata.pagination.current_page + 1
          : false
    }
  )

export const useGetStoresListInfinityQuery = ({ enabled = true, ...params }: TParams) =>
  useInfiniteQuery<AxiosResponse<ResponseSuccess<TStoresListItem[]>>, AxiosError<ResponseError>>(
    [KEY_STORES_LIST_QUERY, params],
    ({ pageParam }) => requests.getStoresList({ ...params, page: pageParam }),
    {
      enabled,
      getNextPageParam: (lastPage) =>
        lastPage.data.metadata.pagination.current_page < lastPage.data.metadata.pagination.last_page
          ? lastPage.data.metadata.pagination.current_page + 1
          : false
    }
  )

export const useGetWalletsManagementDetailsQuery = ({
  leader_std_code
}: {
  leader_std_code: number
}) => {
  return useQuery<
    AxiosResponse<ResponseSuccess<TWalletsManagementDetails>>,
    AxiosError<ResponseError>
  >([KEY_WALLETS_MANAGEMENT_DETAILS_QUERY, leader_std_code], () =>
    requests.getWalletsManagementDetails({ leader_std_code })
  )
}

export const useGetWalletsManagementPeriodQuery = () => {
  return useQuery<
    AxiosResponse<ResponseSuccess<TWalletsManagementPeriod>>,
    AxiosError<ResponseError>
  >([KEY_WALLETS_MANAGEMENT_PERIOD_QUERY], () => requests.getWalletsManagementPeriod())
}

export const useGetWalletsManagementReportQuery = ({
  enabled = true,
  leader_std_code
}: {
  enabled?: boolean
  leader_std_code: number
}) => {
  return useQuery<
    AxiosResponse<ResponseSuccess<TWalletsManagementReport>>,
    AxiosError<ResponseError>
  >(
    [KEY_WALLETS_MANAGEMENT_REPORT_QUERY, leader_std_code],
    () => requests.getWalletsManagementReport({ leader_std_code }),
    { enabled }
  )
}

export const useConcludeWalletsManagementMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<
    AxiosResponse<ResponseSuccess<TConcludeParams>>,
    AxiosError<ResponseError>,
    TConcludeParams
  >(requests.concludeWalletsManagement, {
    onSuccess: () => {
      queryClient.invalidateQueries([KEY_WALLETS_MANAGEMENT_DETAILS_QUERY])

      queryClient.invalidateQueries([KEY_LEADERS_LIST_QUERY])
    }
  })
}

export const useManageWalletMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<
    AxiosResponse<ResponseSuccess<TManageWalletParams>>,
    AxiosError<ResponseError>,
    TManageWalletParams
  >(requests.manageWallet, {
    onSuccess: () => {
      queryClient.invalidateQueries([KEY_WALLETS_MANAGEMENT_DETAILS_QUERY])

      queryClient.invalidateQueries([KEY_STORES_LIST_QUERY])

      queryClient.invalidateQueries([KEY_STAFF_LIST_QUERY])

      queryClient.invalidateQueries([KEY_LEADERS_LIST_QUERY])
    }
  })
}

export const useOpenWalletsPeriodMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<
    AxiosResponse<ResponseSuccess<TOpenWalletsPeriodParams>>,
    AxiosError<ResponseError>,
    TOpenWalletsPeriodParams
  >(requests.openWalletsPeriod, {
    onSuccess: () => {
      queryClient.invalidateQueries([KEY_WALLETS_MANAGEMENT_PERIOD_QUERY])

      queryClient.invalidateQueries([KEY_WALLETS_MANAGEMENT_DETAILS_QUERY])

      queryClient.invalidateQueries([KEY_LEADERS_LIST_QUERY])
    }
  })
}

export const useFinishWalletsPeriodMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<
    AxiosResponse<ResponseSuccess<{ period_id: string }>>,
    AxiosError<ResponseError>,
    { period_id: string }
  >(requests.finishWalletsPeriod, {
    onSuccess: () => {
      queryClient.invalidateQueries([KEY_WALLETS_MANAGEMENT_PERIOD_QUERY])

      queryClient.invalidateQueries([KEY_WALLETS_MANAGEMENT_DETAILS_QUERY])

      queryClient.invalidateQueries([KEY_LEADERS_LIST_QUERY])
    }
  })
}

export const useFlagAsExternalMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<
    AxiosResponse<ResponseSuccess<TStoreWithoutSubsidiaryParams>>,
    AxiosError<ResponseError>,
    TStoreWithoutSubsidiaryParams
  >(requests.flagAsExternal, {
    onSuccess: () => {
      queryClient.invalidateQueries([KEY_STORES_LIST_QUERY])
    }
  })
}

export const useReactivateWalletsPeriodMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<
    AxiosResponse<ResponseSuccess<{ leaderStdCode: number }>>,
    AxiosError<ResponseError>,
    { leaderStdCode: number }
  >(requests.reactivateWalletsPeriod, {
    onSuccess: () => {
      queryClient.invalidateQueries([KEY_WALLETS_MANAGEMENT_PERIOD_QUERY])

      queryClient.invalidateQueries([KEY_WALLETS_MANAGEMENT_DETAILS_QUERY])

      queryClient.invalidateQueries([KEY_STAFF_LIST_QUERY])

      queryClient.invalidateQueries([KEY_LEADERS_LIST_QUERY])
    }
  })
}

export const useSaveReportFileMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<
    AxiosResponse<ResponseSuccess<{ leaderStdCode: number; fileName: string }>>,
    AxiosError<ResponseError>,
    { leaderStdCode: number; fileName: string }
  >(requests.saveReportFile, {
    onSuccess: () => {
      queryClient.invalidateQueries([KEY_WALLETS_MANAGEMENT_REPORT_QUERY])
    }
  })
}
